import React, { useRef, useEffect } from "react";
import { View, Text, TouchableOpacity } from "react-native";
import Colors from "../../../../Resources/Color";
import { getScrollableNodeFromRef } from "../utils/floatingUtils";
import { HighlightedText } from "./HighlightedText";

/**
 * Slot Row component for displaying protocol slot suggestions
 */
export const SlotRow = ({
  result,
  isSelected,
  onSelect,
  onHover,
  searchQuery,
  t,
  language,
  index,
  totalResults,
  scrollContainerRef,
  keyboardNavigationRef,
}) => {
  const rowRef = useRef(null);

  const primaryExercise = result.primaryExercise;
  const secondaryExercise = result.secondaryExercise;

  // Get exercise names
  const primaryName = primaryExercise?.getName?.(t, language) || "";
  const secondaryName = secondaryExercise?.getName?.(t, language) || "";
  const displayName = secondaryName
    ? `${primaryName} + ${secondaryName}`
    : primaryName;

  // Get set counts
  const primarySetCount = primaryExercise?.series?.length || 0;
  const secondarySetCount = secondaryExercise?.series?.length || 0;

  // Format set count text with proper singular/plural
  const formatSetCount = (count) => {
    if (count === 1) return `1 ${t("setSingular-string")}`;
    return `${count} ${t("sets-string")}`;
  };

  const setCountText = secondaryExercise
    ? `${formatSetCount(primarySetCount)} + ${formatSetCount(
        secondarySetCount,
      )}`
    : formatSetCount(primarySetCount);

  // Get priority-based colors from protocolSlot
  const getPriorityColors = (protocolSlot) => {
    if (!protocolSlot) {
      return {
        color: Colors.grayColor,
        bg: Colors.lightGrayColor,
      };
    }

    // Extract priority from protocolSlot string
    const raw = String(protocolSlot).replace(/-string$/, "");
    const idx = raw.search(/[A-Z]/);
    const priority = idx > 0 ? raw.substring(0, idx).toLowerCase() : "";

    switch (priority) {
      case "primary":
        return {
          color: "rgb(230, 0, 0)",
          bg: "rgba(230, 0, 0, 0.1)",
        };
      case "secondary":
        return {
          color: "rgb(63, 123, 141)",
          bg: "rgba(63, 123, 141, 0.1)",
        };
      case "tertiary":
        return {
          color: "rgb(34, 139, 34)",
          bg: "rgba(34, 139, 34, 0.1)",
        };
      case "quaternary":
        return {
          color: "rgb(255, 165, 0)",
          bg: "rgba(255, 165, 0, 0.1)",
        };
      case "quinary":
        return {
          color: "rgb(255, 105, 180)",
          bg: "rgba(255, 105, 180, 0.1)",
        };
      default:
        return {
          color: Colors.grayColor,
          bg: Colors.lightGrayColor,
        };
    }
  };

  const priorityColors = getPriorityColors(result.protocolSlot);

  // Scroll into view when selected
  useEffect(() => {
    if (
      isSelected &&
      keyboardNavigationRef?.current &&
      rowRef.current &&
      scrollContainerRef?.current
    ) {
      const isFirstItem = index === 0;
      const isLastItem = index === totalResults - 1;
      const rowElement = rowRef.current;

      const scrollContainer = getScrollableNodeFromRef(scrollContainerRef);

      if (isFirstItem) {
        scrollContainerRef.current?.scrollTo?.({ y: 0, animated: true });
        if (scrollContainer) {
          scrollContainer.scrollTop = 0;
        }
        return;
      }

      if (isLastItem) {
        scrollContainerRef.current?.scrollToEnd?.({ animated: true });
        if (scrollContainer) {
          const containerHeight = scrollContainer.clientHeight;
          const scrollHeight = scrollContainer.scrollHeight;
          scrollContainer.scrollTop = scrollHeight - containerHeight;
        }
        return;
      }

      rowElement.scrollIntoView?.({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [
    isSelected,
    index,
    totalResults,
    scrollContainerRef,
    keyboardNavigationRef,
  ]);

  return (
    <TouchableOpacity
      ref={rowRef}
      onPress={onSelect}
      onMouseEnter={onHover}
      style={{
        flexDirection: "row",
        alignItems: "flex-start",
        justifyContent: "space-between",
        paddingVertical: 10,
        paddingHorizontal: 12,
        marginHorizontal: 8,
        borderRadius: 10,
        backgroundColor: isSelected ? "rgba(255, 193, 7, 0.12)" : "transparent",
        outlineStyle: "none",
      }}
    >
      {/* Left side: Exercise name(s) + set count */}
      <View
        style={{
          flex: 1,
          minWidth: 0,
          marginRight: 10,
        }}
      >
        {/* Exercise name with highlighting */}
        {searchQuery ? (
          <HighlightedText text={displayName} searchQuery={searchQuery} />
        ) : (
          <Text style={{ fontSize: 13, color: Colors.blackColor }}>
            {displayName}
          </Text>
        )}

        {/* Set count */}
        <Text
          style={{
            fontSize: 11,
            color: Colors.grayColor,
            marginTop: 4,
          }}
        >
          {setCountText}
        </Text>
      </View>

      {/* Right side: Slot type pill with priority-based colors */}
      <View
        style={{
          backgroundColor: priorityColors.bg,
          paddingHorizontal: 8,
          paddingVertical: 3,
          borderRadius: 8,
          alignSelf: "flex-start",
        }}
      >
        <Text
          style={{
            fontSize: 11,
            fontWeight: "500",
            color: priorityColors.color,
          }}
        >
          {t(result.protocolSlot)}
        </Text>
      </View>
    </TouchableOpacity>
  );
};
